/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { convertProductToProductSearchHit } from '@/lib/product/product-conversion';

interface PurchaseThemSeparatelyProps {
    childProducts: ShopperProducts.schemas['Product'][];
}

/**
 * Informational upsell grid showing each of the bundle's contents as an individually-purchasable
 * `ProductTile` (same card used on PLP/PDP recommendation rails) — not a selection UI. The bundle itself is
 * added as a fixed configuration via `ProductBundleDetails`; this section exists purely to let a shopper buy
 * one item on its own instead of the whole bundle.
 */
export default function PurchaseThemSeparately({ childProducts }: PurchaseThemSeparatelyProps): ReactElement | null {
    const { t } = useTranslation('product');

    const searchHits = useMemo(
        () => childProducts.map((childProduct) => convertProductToProductSearchHit(childProduct)),
        [childProducts]
    );

    if (searchHits.length === 0) {
        return null;
    }

    return (
        <section className="space-y-6">
            <h2 className="text-2xl font-bold text-card-foreground tracking-tight">{t('purchaseThemSeparately')}</h2>
            <ProductTileProvider>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
                    {searchHits.map((hit) => (
                        <ProductTile key={hit.productId} product={hit} />
                    ))}
                </div>
            </ProductTileProvider>
        </section>
    );
}
